import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getLanguages } from '../services/dataService';
import appLogo from '../assets/branding/logo.png';
import appLogoAlt from '../assets/branding/logo2.png';

const activityLinks = [
  { to: '/activities/walking',   label: 'Walking',   icon: '🚶', desc: 'Distance & time'    },
  { to: '/activities/gym',       label: 'Gym',        icon: '🏋️', desc: 'Strength training'  },
  { to: '/activities/badminton', label: 'Badminton',  icon: '🏸', desc: 'Court sessions'     },
  { to: '/activities/skating',   label: 'Skating',    icon: '⛸️', desc: 'Skate distance'     },
];

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const LANGS = getLanguages() ?? [];
  const currentCode = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLang = LANGS.find(l => l.code === currentCode) || LANGS[0];

  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const inActivities = location.pathname.startsWith('/activities');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
    <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-xs text-amber-800">
      {t('disclaimer')}
    </div>
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/92 backdrop-blur-lg">
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">

        {/* Brand + desktop links */}
        <div className="flex items-center gap-4">
          <Link to="/" className="group flex min-w-0 items-center gap-2.5">
            <img
              src={appLogo}
              alt="Vivifyer"
              className="h-9 w-9 rounded-xl object-cover ring-2 ring-emerald-100 transition group-hover:ring-emerald-300"
            />
            <span className="truncate text-base font-black text-slate-900 sm:text-lg">
              <span className="bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] bg-clip-text text-transparent">
                Vivifyer
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            <Link
              to="/"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.home')}
              {isActive('/') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            {/* Activities dropdown */}
            <div className="group relative">
              <button
                className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  inActivities ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {t('nav.activities')}
                <svg className="ml-1 inline-block h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                {inActivities && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
              </button>

              <div className="invisible absolute left-0 top-full mt-2.5 w-64 rounded-2xl border border-slate-200 bg-white p-2 opacity-0 shadow-2xl shadow-slate-900/10 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                <div className="mb-1 px-3 pb-2 pt-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Track an activity
                </div>
                {activityLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-slate-50 ${
                      isActive(item.to) ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <span className="mt-0.5 text-lg leading-none">{item.icon}</span>
                    <span>
                      <p className={`text-sm font-bold leading-tight ${isActive(item.to) ? 'text-[var(--brand-primary)]' : 'text-slate-800'}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/history"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/history') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.history')}
              {isActive('/history') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            <Link
              to="/schedule"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/schedule') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.schedule')}
              {isActive('/schedule') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            <Link
              to="/steps"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/steps') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.steps')}
              {isActive('/steps') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            <Link
              to="/sports"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/sports') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.sports')}
              {isActive('/sports') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            <Link
              to="/meals"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/meals') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              Meals
              {isActive('/meals') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            <Link
              to="/habits"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/habits') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              Habits
              {isActive('/habits') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>

            <Link
              to="/analytics"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/analytics') ? 'text-violet-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              Analytics
              {isActive('/analytics') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-violet-600" />}
            </Link>

            <Link
              to="/download"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/download') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              Download the app
              {isActive('/download') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[var(--brand-primary)]" />}
            </Link>
          </div>
        </div>

        {/* Right side */}
        <div className="hidden items-center gap-2 md:flex">
          {/* Language switcher */}
          <div className="group relative">
            <button className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-100">
              <span>{currentLang?.flag}</span>
              <span>{currentLang?.nativeLabel || currentLang?.code?.toUpperCase()}</span>
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className="invisible absolute right-0 top-full z-50 mt-1 max-h-80 w-52 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 opacity-0 shadow-lg transition-all duration-150 group-hover:visible group-hover:opacity-100">
              {LANGS.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold transition hover:bg-slate-50 ${lang.code === currentCode ? 'bg-emerald-50 text-emerald-600' : 'text-slate-700'}`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span className="min-w-0 flex-1 truncate">{lang.nativeLabel}</span>
                  <span className="text-[10px] font-black text-slate-400">{lang.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
          <Link
            to="/log"
            className="rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] px-4 py-2 text-sm font-bold text-white shadow-md shadow-emerald-600/20 transition hover:-translate-y-px hover:shadow-emerald-600/40"
          >
            + {t('nav.log')}
          </Link>
          {isAuthenticated ? (
            <>
              <Link
                to="/profile"
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] text-xs font-black text-white">
                  {(user?.name || user?.email || '?')[0].toUpperCase()}
                </span>
                <span className="max-w-[120px] truncate">{user?.name || user?.email}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="rounded-xl px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] px-4 py-2 text-sm font-bold text-white shadow-md shadow-emerald-600/20 transition hover:-translate-y-px"
              >
                {t('nav.getStarted')}
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setIsMenuOpen((v) => !v)}
          className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 md:hidden"
          aria-label="Toggle menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {isMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </nav>

      {/* Mobile drawer */}
      {isMenuOpen && (
        <div className="border-t border-slate-100 bg-white/98 backdrop-blur-lg md:hidden">
          <div className="space-y-1 px-4 py-4">
            <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 p-2.5">
              <img
                src={appLogoAlt}
                alt="Vivifyer"
                className="h-9 w-9 rounded-lg object-cover ring-1 ring-emerald-200"
              />
              <div>
                <p className="text-sm font-black text-slate-900">Vivifyer</p>
                <p className="text-xs text-slate-500">Personal Fitness Tracker</p>
              </div>
            </div>

            <Link
              to="/"
              className={`block rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {t('nav.home')}
            </Link>

            <Link
              to="/log"
              className="block rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] px-3 py-2.5 text-center text-sm font-bold text-white"
            >
              + {t('nav.log')}
            </Link>

            <div className="px-3 pb-1 pt-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
              {t('nav.activities')}
            </div>
            {activityLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                  isActive(item.to) ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            ))}

            <Link
              to="/history"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/history') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>📋</span>
              <span>{t('nav.history')}</span>
            </Link>

            <div className="px-3 pb-1 pt-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
              {t('nav.fitnessHub')}
            </div>

            <Link
              to="/schedule"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/schedule') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>📅</span>
              <span>{t('nav.gymSchedule')}</span>
            </Link>

            <Link
              to="/steps"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/steps') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>👟</span>
              <span>{t('nav.stepTracker')}</span>
            </Link>

            <Link
              to="/sports"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/sports') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>🏅</span>
              <span>{t('nav.sportsHub')}</span>
            </Link>

            <Link
              to="/meals"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/meals') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>🍽️</span>
              <span>Meal Planner</span>
            </Link>

            <Link
              to="/habits"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/habits') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>✅</span>
              <span>Habits</span>
            </Link>

            <Link
              to="/analytics"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/analytics') ? 'bg-violet-50 text-violet-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>📊</span>
              <span>Analytics</span>
            </Link>

            <Link
              to="/download"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive('/download') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>📲</span>
              <span>Download the app</span>
            </Link>

            <div className="mt-3 border-t border-slate-100 pt-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                      isActive('/profile') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] text-xs font-black text-white">
                      {(user?.name || user?.email || '?')[0].toUpperCase()}
                    </span>
                    <span>Profile &amp; Stats</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/login"
                    className="rounded-xl border border-slate-200 py-2.5 text-center text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] py-2.5 text-center text-sm font-bold text-white"
                  >
                    {t('nav.getStarted')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
    </>
  );
}
